import styled from '@emotion/styled'
import { DeviceType } from '@pubstack/common/src/device-type'
import { byLabel } from '@pubstack/common/src/sort'
import { LazyLoadingRuleConfig } from '@pubstack/common/src/stack'
import { capitalizeFirstLetter } from '@pubstack/common/src/stringUtils'
import { FunctionComponent, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { StackFormData } from '~/modules/adm/inventory/sites/stack/StackEdit'
import { WithClassName } from '~/types/utils'
import { ToggleWithText } from './ToggleWithText'

type LazyLoadingRuleSelectorProps = WithClassName & {
  availableRules: SelectOptionProp<string>[]
  availableDevices: DeviceType[]
  description?: string
  disabledSelector?: boolean
}

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 760px;
`

const BaseLazyLoadingRuleSelector: FunctionComponent<LazyLoadingRuleSelectorProps> = ({ availableRules, availableDevices, description, disabledSelector }) => {
  const { control } = useFormContext<StackFormData>()
  const formData = useWatch({ control, name: 'configuration.lazyLoadingRuleConfig' })

  const [isToggleChecked, setIsToggleChecked] = useState(!!formData)
  const [formDataMemo, setFormDataMemo] = useState<LazyLoadingRuleConfig[] | undefined>(formData ?? availableDevices.map((device) => ({ device, ruleId: '' })))

  return (
    <Controller
      control={control}
      name={'configuration.lazyLoadingRuleConfig'}
      render={({ field: { onChange }, formState: { errors } }) => {
        return (
          <>
            <ToggleWithText
              checked={isToggleChecked}
              onChange={(isChecked) => {
                setIsToggleChecked(isChecked)
                if (!isChecked) {
                  onChange(undefined)
                  setFormDataMemo(formData)
                } else {
                  onChange(formDataMemo)
                }
              }}
              title={'Lazy loading'}
              trackingLabel={isToggleChecked ? 'adm-sites-stacks-form-lazy-loading-unset' : 'adm-sites-stacks-form-lazy-loading-set'}
              description={description}
              disabled={disabledSelector}
            />
            {isToggleChecked && (
              <SelectWrapper>
                {availableDevices.map((device, index) => (
                  <SelectWrapper key={index}>
                    <Select
                      searchable
                      isMessageBlock
                      rules={{ required: `You must select a rule for ${device}.` }}
                      error={errors?.configuration?.lazyLoadingRuleConfig?.[index]?.ruleId?.message}
                      key={index}
                      control={control}
                      name={`configuration.lazyLoadingRuleConfig.${index}.ruleId`}
                      label={`${capitalizeFirstLetter(device)} rule`}
                      options={[...availableRules].sort(byLabel).map((rule) => ({
                        value: rule.value,
                        label: rule.label,
                        trackingLabel: rule.value === 'NO_RULE' ? `adm-sites-stacks-lazy-loading-${device}-no-rule-set` : `adm-sites-stacks-lazy-loading-${device}-rule-set`,
                      }))}
                    />
                  </SelectWrapper>
                ))}
              </SelectWrapper>
            )}
          </>
        )
      }}
    />
  )
}

export const LazyLoadingRuleSelector = styled(BaseLazyLoadingRuleSelector)``
